/*
 * The icon concept registry — the ONE module in this repo allowed to import lucide-react
 * (design-system §9; FS-3 AC-21, which greps for the import and expects only this file to match).
 *
 * Why a registry rather than importing icons where they are used: an icon is a *concept* in this
 * product ("retry", "close"), not a drawing. Naming the concept once means the drawing can change
 * in one place, two screens cannot pick different glyphs for the same idea, and swapping icon
 * libraries later is an edit to this file instead of a sweep. It is also the cheapest way to hold
 * FS-3 AC-21 — one import site is greppable, forty are a review obligation.
 *
 * This file stays free of JSX so it can stay `.ts`: it is the concept map, and `Icon.tsx` beside it
 * is the component that draws one. That split is also what keeps the lucide import surface to a
 * single module rather than to every file that happens to render a glyph.
 *
 * The set below is deliberately small: every key is a concept a §5 component row or a §2 tone
 * actually renders this slice. §0's rule of two — a concept nobody draws yet is added when the
 * screen that needs it arrives, not in anticipation. Note the absence of `info`: §2 has no `info`
 * tone (Badge has no info variant), so an info icon would invite a tone that does not exist.
 */
import {
    ArrowLeft,
    Bold,
    Braces,
    Building2,
    Calendar,
    CalendarCheck,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsUpDown,
    ChevronUp,
    Contact,
    Copy,
    Download,
    Ellipsis,
    ExternalLink,
    FileImage,
    FileSpreadsheet,
    FileText,
    Inbox,
    Italic,
    Leaf,
    Link,
    List,
    ListOrdered,
    LoaderCircle,
    Lock,
    LogOut,
    Mail,
    Mails,
    Minus,
    Package,
    PanelLeft,
    Paperclip,
    Pencil,
    Phone,
    Plus,
    Presentation,
    RefreshCw,
    Search,
    Settings,
    ShieldCheck,
    Sparkles,
    Star,
    StickyNote,
    Tag,
    TriangleAlert,
    Upload,
    User,
    UserPlus,
    Users,
    X,
} from 'lucide-react';

/*
 * Concept -> glyph. `as const satisfies` rather than a type annotation: `satisfies` checks the
 * shape without widening the value, so `IconName` below stays the literal union of these keys and
 * a typo at a call site is a compile error rather than a missing icon at runtime.
 */
export const iconRegistry = {
    /** Dismiss — the ✕ on a modal or a removable chip (§5 Modal, Button/ghost-icon). */
    close: X,
    /** Add one more to a list — the *Add tag* chip at the end of a record's tags. */
    add: Plus,
    /** Search — the leading glyph in the search-size Input (§5 Input). */
    search: Search,
    /** Expand a collapsed surface — dropdown, combobox, select chrome (§5). */
    expand: ChevronDown,
    /** Retry after a failure — the action §5's error state requires. */
    retry: RefreshCw,
    /** Waiting for an answer — shadcn's `Spinner` (`src/ui/shadcn/spinner.tsx`), in a busy button (§5 Busy). */
    spinner: LoaderCircle,
    /** Nothing here yet — the glyph §5's empty state requires. */
    empty: Inbox,
    /** Something failed (`tone/danger`). */
    error: TriangleAlert,
    /** Attention, not failure (`tone/notice`). */
    warning: TriangleAlert,
    /** Done / met (`tone/positive`). */
    success: Check,
    /** Accounts — the buyer-organisation section (§6 sidebar nav). */
    account: Building2,
    /** Contacts — the people at buyer organisations (§6 sidebar nav; F1-f). */
    contact: Contact,
    /** Leads — the lead-intake/triage section (§6 sidebar nav). */
    lead: UserPlus,
    /** Activities — the follow-up tasks on accounts and leads (§6 sidebar nav; F3-a FR-V7). */
    activity: CalendarCheck,
    /** Email — the mailbox section (§6 sidebar nav). */
    email: Mail,
    /** A phone number — the glyph before it on a contact's row. */
    phone: Phone,
    /** A record's notes — the *Notes and tags* icon button on a contact's row. */
    notes: StickyNote,
    /** The primary one of several — the account's primary contact. */
    primary: Star,
    /** Copy a value to the clipboard — the contact page's email address and phone number. */
    copy: Copy,
    /** When a record was added — the last line of the contact page's profile card. */
    added: Calendar,
    /** Change a link in place — the lead's Customer card *Change*. */
    edit: Pencil,
    /** One person — the contact a lead is linked to. */
    person: User,
    /** A record the viewer cannot open — named only as *An account you cannot open*. */
    noAccess: Lock,
    /** A tea sample sent or received — its entry on an account's timeline. */
    sample: Leaf,
    /** A confirmed order — its entry on an account's timeline. */
    order: Package,
    /** Brought over from another system — a note imported from Pipedrive. */
    imported: Download,
    /** Pricing — the Buying Standards book (§6 sidebar nav; PRD Rev 27, no products catalog). */
    pricing: Tag,
    /** Users — the user-admin section (§6 sidebar nav). */
    users: Users,
    /** Roles — the role-management section (§6 sidebar nav). */
    roles: ShieldCheck,
    /** Collapse/expand the sidebar — the §6 sidebar toggle, in the sidebar's header. */
    sidebarToggle: PanelLeft,
    /** Back navigation — the glyph of the §6 back arrow beside a page's title (BackLink). */
    back: ArrowLeft,
    /** Sign out — the leading glyph on the current-user menu's single action (§6 sidebar footer). */
    signout: LogOut,
    /** Settings for the section a screen belongs to — the header settings affordance (§8, F20-b AC-25b). */
    settings: Settings,
    /** The message carries a file — the inbox row's attachment indicator (F20-b AC-20b). */
    attachment: Paperclip,
    /** Choose or drop a file to upload — the §5 file-upload drop zone (F26-c, the weekly workbook). */
    upload: Upload,
    /** A text document — a PDF or Word file's type icon in the §5 attachment row (F20-h). */
    fileText: FileText,
    /** A spreadsheet — an Excel file's type icon in the §5 attachment row (F20-h). */
    fileSpreadsheet: FileSpreadsheet,
    /** A slide deck — a PowerPoint file's type icon in the §5 attachment row (F20-h). */
    filePresentation: Presentation,
    /** An image — a JPEG or PNG file's type icon in the §5 attachment row (F20-h). */
    fileImage: FileImage,
    /** The active column is sorted ascending — the §6 sortable header's chevron (F1-b). */
    sortAscending: ChevronUp,
    /** The active column is sorted descending — the §6 sortable header's chevron (F1-b). */
    sortDescending: ChevronDown,
    /** A column that can be sorted but is not — the faint hint on a sortable header under the pointer or focus. */
    sortable: ChevronsUpDown,
    /** Some, not all — the mixed checkbox's glyph, in place of the tick (§6 Selection, F5-h WI-1.3). */
    indeterminate: Minus,
    /** Bold text — the rich-text body's toolbar (§5 *Rich-text body*, F20-c's compose dialog). */
    bold: Bold,
    /** Italic text — the rich-text body's toolbar (§5 *Rich-text body*). */
    italic: Italic,
    /** A bulleted list — the rich-text body's toolbar (§5 *Rich-text body*). */
    bulletList: List,
    /** A numbered list — the rich-text body's toolbar (§5 *Rich-text body*). */
    numberedList: ListOrdered,
    /** A link — the rich-text body's toolbar (§5 *Rich-text body*). */
    link: Link,
    /** shadcn/ui (AD #39): the chosen option's mark in a shadcn Select or Command list. */
    selected: Check,
    /** shadcn/ui (AD #39): a shadcn Select list's scroll-up control. */
    scrollUp: ChevronUp,
    /** shadcn/ui (AD #39): a shadcn Select list's scroll-down control. */
    scrollDown: ChevronDown,
    /** shadcn/ui (AD #39): the shadcn Pagination's Previous control. */
    previousPage: ChevronLeft,
    /** shadcn/ui (AD #39): the shadcn Pagination's Next control. */
    nextPage: ChevronRight,
    /** shadcn/ui (AD #39): the shadcn Pagination's gap between page numbers. */
    morePages: Ellipsis,
    /** shadcn/ui (AD #39): the mark between two places in a page's breadcrumb trail. */
    breadcrumbSeparator: ChevronRight,
    /** A table row's ⋯ menu — View, Edit and the row's own actions (design-system §6 List screen, Rev 70). */
    rowActions: Ellipsis,
    /**
     * Email templates — the Settings page's *Email templates* card and the compose dialog's *Insert template* (F20-d):
     * stacked envelopes, copies of one email kept to reuse, distinct from `email`'s single envelope.
     */
    emailTemplate: Mails,
    /** A merge field — the template editor's *Insert merge field* (F20-d). */
    mergeField: Braces,
    /** An AI draft — the compose dialog's *AI draft* and the lead's *Draft follow-up* (F11-h; the prototype's sparkle). */
    aiDraft: Sparkles,
    /** An AI account briefing — the account page's *Prepare briefing* (F13; the prototype's sparkle on the briefing). */
    aiBriefing: Sparkles,
    /** AI reading a pasted inquiry into a lead — the leads page's *New lead from inquiry* (F12; the same sparkle). */
    aiExtraction: Sparkles,
    /** A link that opens another site in a new tab — each research finding's source link, `ExternalLink` (F11-c FR-V2). */
    externalLink: ExternalLink,
} as const satisfies Record<string, unknown>;

/** Every concept the product may draw. Adding a glyph means adding a key here first. */
export type IconName = keyof typeof iconRegistry;

/** The concept keys as a value, for tests and for any UI that enumerates them. */
export const iconNames = Object.keys(iconRegistry) as readonly IconName[];
